import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { ISuggestion } from '../models';

/**
 * Type that represents objects with an id property (compatible with ISuggestion and test mocks)
 */
type SuggestionWithId = Pick<ISuggestion, 'id'>;

/**
 * Service to clean up orphaned track-change suggestion markers from HTML content.
 * A suggestion marker is "orphaned" when its ID appears in the HTML but not in the
 * suggestions array returned by the API.
 *
 * Shared between the load path (decorators.effects.ts) and save path (editor.component.ts).
 */
@Injectable({ providedIn: 'root' })
export class SuggestionCleanupService {
  /**
   * Removes all suggestion markers (tags and data-suggestion-* attributes) whose IDs
   * are NOT present in the validSuggestions array.
   *
   */
  cleanupOrphanedSuggestionMarkers(
    content: string | null,
    validSuggestions: SuggestionWithId[] | null | undefined
  ): string | null {
    if (!content || !validSuggestions) {
      return content;
    }
    const validSuggestionIds = new Set(
      _.map(validSuggestions, (validSuggestion) => validSuggestion.id)
    );
    let cleanedContent = content;

    const allSuggestionIdsInContent =
      this.extractSuggestionIdsFromContent(content);
    const orphanedIds = _.filter(
      allSuggestionIdsInContent,
      (id) => !validSuggestionIds.has(id)
    );
    orphanedIds.length && console.log('orphanedIds >>>>', orphanedIds);
    if (orphanedIds.length === 0) {
      return content;
    }

    _.forEach(orphanedIds, (orphanedId) => {
      cleanedContent = this.removeSuggestionMarkersById(
        cleanedContent,
        orphanedId
      );
    });

    return cleanedContent;
  }

  /**
   * Extracts all unique suggestion IDs from HTML content.
   * Matches patterns like insertion:ID:user, deletion:ID:user, deletion:deleteLink:ID:user, etc.
   */
  extractSuggestionIdsFromContent(content: string | null): string[] {
    if (!content) {
      return [];
    }

    const suggestionIds = new Set<string>();

    const suggestionPattern =
      /(?:attribute:alignment\|[^:"'<>\s,]+(?::[^:"'<>\s,]+){2}|deletion:deleteLink:[^:"'<>\s,]+:[^:"'<>\s,]+|(?:insertion|deletion|formatInline|formatBlock|merge|split):[^:"'<>\s,]+(?::[^:"'<>\s,]+){1,2})/g;

    let match;
    while ((match = suggestionPattern.exec(content)) !== null) {
      const suggestionId = this.getSuggestionId(match[0]);
      if (suggestionId) {
        suggestionIds.add(suggestionId);
      }
    }

    return Array.from(suggestionIds);
  }

  /**
   * Removes all suggestion markers (tags and attributes) for a specific suggestion ID.
   */
  private removeSuggestionMarkersById(
    content: string,
    suggestionId: string
  ): string {
    let cleanedContent = content;

    cleanedContent = this.removeSuggestionEntryFromTag(
      cleanedContent,
      'suggestion-start',
      suggestionId
    );
    cleanedContent = this.removeSuggestionEntryFromTag(
      cleanedContent,
      'suggestion-end',
      suggestionId
    );

    cleanedContent = this.removeSuggestionEntryFromAttribute(
      cleanedContent,
      'data-suggestion-start-before',
      suggestionId
    );
    cleanedContent = this.removeSuggestionEntryFromAttribute(
      cleanedContent,
      'data-suggestion-end-after',
      suggestionId
    );
    cleanedContent = this.removeSuggestionEntryFromAttribute(
      cleanedContent,
      'data-suggestion-start-after',
      suggestionId
    );
    cleanedContent = this.removeSuggestionEntryFromAttribute(
      cleanedContent,
      'data-suggestion-end-before',
      suggestionId
    );

    return cleanedContent;
  }

  /**
   * Removes entries matching the given suggestionId from a data-suggestion-* attribute.
   * If no entries remain, the entire attribute is removed.
   */
  private removeSuggestionEntryFromAttribute(
    content: string,
    attributeName: string,
    suggestionId: string
  ): string {
    const escapedAttr = attributeName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const attributeRegex = new RegExp(`(\\s*)${escapedAttr}="([^"]*)"`, 'gi');

    return content.replace(
      attributeRegex,
      (fullMatch, leadingWhitespace, value) => {
        const entries = value.split(',');
        const remaining = entries.filter((entry) => {
          if (this.isRetainedMarker(entry)) {
            return true;
          }
          const idInEntry = this.getSuggestionId(entry);
          return idInEntry !== suggestionId;
        });

        if (remaining.length === 0) {
          return '';
        }
        if (remaining.length === entries.length) {
          return fullMatch;
        }
        return `${leadingWhitespace}${attributeName}="${remaining.join(',')}"`;
      }
    );
  }

  /**
   * Removes entries matching the given suggestionId from the name attribute of
   * suggestion-start / suggestion-end tags. If no entries remain, the entire tag is removed.
   */
  private removeSuggestionEntryFromTag(
    content: string,
    tagName: string,
    suggestionId: string
  ): string {
    const escapedTag = tagName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const tagRegex = new RegExp(
      `<${escapedTag}(\\s[^>]*?)?\\sname="([^"]*)"([^>]*?)(\\s*/?)>(\\s*<\\/${escapedTag}>)?`,
      'gi'
    );

    return content.replace(
      tagRegex,
      (fullMatch, beforeName, value, afterName, selfCloseToken, closingTag) => {
        const entries = value.split(',');
        const remaining = entries.filter((entry) => {
          if (this.isRetainedMarker(entry)) {
            return true;
          }
          const idInEntry = this.getSuggestionId(entry);
          return idInEntry !== suggestionId;
        });

        if (remaining.length === 0) {
          return '';
        }
        if (remaining.length === entries.length) {
          return fullMatch;
        }
        const head = beforeName ?? '';
        const tail = afterName ?? '';
        const close = selfCloseToken ?? '';
        const endTag = closingTag ?? '';
        return `<${tagName}${head} name="${remaining.join(
          ','
        )}"${tail}${close}>${endTag}`;
      }
    );
  }

  private getSuggestionId(marker: string): string | undefined {
    const alignmentMatch = /^attribute:alignment\|([^:]+)/.exec(marker);
    if (alignmentMatch) {
      return alignmentMatch[1];
    }

    const parts = marker.split(':');
    const usesThirdPartAsSuggestionId =
      (parts[0] === 'deletion' && parts[1] === 'deleteLink') ||
      (parts[0] === 'formatBlock' && parts.length >= 4);

    return usesThirdPartAsSuggestionId ? parts[2] : parts[1];
  }

  private isRetainedMarker(marker: string): boolean {
    return marker.startsWith('attribute:');
  }
}
